import React, { useState } from 'react';
import './StartDashboard.css';
import { ROOM_TYPES } from '../../components/Gallery/Scene/config/roomTypes';

function StartDashboard({ onCreateGallery, onClose, galleries, isLoading, onGallerySelect, onDeleteGallery, showBackToGallery = false, currentGalleryName = null }) {
  const [newGalleryName, setNewGalleryName] = useState('');
  const [selectedRoomType, setSelectedRoomType] = useState(ROOM_TYPES.CLASSIC);
  const [customGlbPath, setCustomGlbPath] = useState('/rooms/RoomTemplate.glb');
  const [activeView, setActiveView] = useState('galleries'); // 'galleries', 'create'
  const [sortBy, setSortBy] = useState('recent');
  const [showPrivateOnly, setShowPrivateOnly] = useState(false);
  const [galleryToDelete, setGalleryToDelete] = useState(null);
  const [roomSelectionTab, setRoomSelectionTab] = useState('all'); // 'all' or 'owned'
  const [searchQuery, setSearchQuery] = useState('');

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newGalleryName.trim()) return;

    try {
      console.log('Creating new gallery with name:', newGalleryName);
      console.log('Selected room type:', selectedRoomType);
      
      // If custom GLB room type or hex-glb is selected, pass the GLB path
      if (selectedRoomType === 'custom-glb' || selectedRoomType === 'hex-glb') {
        console.log('Using custom GLB path:', customGlbPath);
        await onCreateGallery(newGalleryName, 'custom-glb', customGlbPath);
      } else {
        await onCreateGallery(newGalleryName, selectedRoomType);
      }
      
      setNewGalleryName('');
      setSelectedRoomType(ROOM_TYPES.CLASSIC);
      setCustomGlbPath('/rooms/RoomTemplate.glb');
      setActiveView('galleries');
      onClose();
    } catch (error) {
      console.error('Failed to create gallery:', error);
    }
  };
  
  const handleRoomSelect = (roomId, glbPath) => {
    setSelectedRoomType(roomId);
    if (glbPath) {
      setCustomGlbPath(glbPath);
    }
  };

  const handleGalleryClick = async (gallery, e) => {
    // Prevent gallery selection when clicking delete button
    if (e.target.closest('.delete-button')) return;
    
    try {
      await onGallerySelect(gallery);
      onClose();
    } catch (error) {
      console.error('Failed to select gallery:', error);
    }
  };

  const handleDeleteClick = (gallery, e) => {
    e.stopPropagation();
    setGalleryToDelete(gallery);
  };

  const handleConfirmDelete = async () => {
    try {
      await onDeleteGallery(galleryToDelete._id);
      setGalleryToDelete(null);
    } catch (error) {
      console.error('Failed to delete gallery:', error);
    }
  };

  const sortGalleries = (galleries) => {
    return [...galleries].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return (a.isPublic === b.isPublic) ? 0 : a.isPublic ? -1 : 1;
        case 'recent':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });
  };

  const filterGalleries = (galleries) => {
    let filtered = galleries;
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(gallery => 
        gallery.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // BETA: Private filtering disabled for launch
    // if (showPrivateOnly) {
    //   filtered = filtered.filter(gallery => !gallery.isPublic);
    // }
    
    return filtered;
  };

  const displayGalleries = filterGalleries(sortGalleries(galleries));

  // Define room options with thumbnails and categories
  const roomOptions = [
    {
      id: 'free',
      title: 'Free Rooms',
      rooms: [
        {
          id: ROOM_TYPES.CLASSIC,
          name: 'Classic',
          description: 'The classic gallery room with standard dimensions.',
          thumbnail: `${process.env.PUBLIC_URL}/static/media/thumb-classic.png`,
          type: 'free'
        },
        // BETA: Spacious room hidden for launch - uncomment to re-enable
        // {
        //   id: ROOM_TYPES.SPACIOUS,
        //   name: 'Spacious',
        //   description: 'A spacious, dark-themed conference hall style gallery with larger dimensions.',
        //   thumbnail: '/rooms/thumbnails/spacious.jpg', // Placeholder path
        //   type: 'free'
        // },
        {
          id: ROOM_TYPES.HEXAGONAL,
          name: 'Hexagonal',
          description: 'A modern hexagonal room with six walls for displaying your art.',
          thumbnail: `${process.env.PUBLIC_URL}/static/media/thumb-hex.jpg`,
          type: 'free'
        }
      ]
    },
    {
      id: 'premium',
      title: 'Premium Rooms',
      subtitle: 'Coming Soon',
      comingSoon: true,
      rooms: [
        // BETA: Premium rooms hidden for launch - uncomment to re-enable
        // {
        //   id: 'custom-glb',
        //   name: 'Custom Template',
        //   description: 'A custom room loaded from a GLB file. Perfect for unique gallery experiences.',
        //   thumbnail: '/rooms/thumbnails/custom.jpg', // Placeholder path
        //   type: 'premium',
        //   glbPath: '/rooms/RoomTemplate.glb',
        //   owned: true // For demonstration, we'll assume the user owns this template
        // },
        // {
        //   id: 'hex-glb',
        //   name: 'Hexagonal GLB',
        //   description: 'A custom hexagonal room loaded from a GLB file. Modern design with improved lighting.',
        //   thumbnail: '/rooms/thumbnails/hexagonal.jpg', // Using the same thumbnail as regular hexagonal
        //   type: 'premium',
        //   glbPath: '/rooms/HexRoom.glb',
        //   owned: true // For demonstration, we'll assume the user owns this template
        // }
        // More premium rooms would be added here in the future
      ]
    }
  ];
  
  // Filter rooms based on the selected tab
  const getFilteredRooms = () => {
    if (roomSelectionTab === 'all') {
      return roomOptions;
    } else {
      // Only show owned rooms
      return roomOptions.map(category => ({
        ...category,
        rooms: category.rooms.filter(room => room.owned)
      })).filter(category => category.rooms.length > 0);
    }
  };

  return (
    <div className="start-dashboard">
      <div className="start-dashboard__content">
        {showBackToGallery && currentGalleryName ? (
          <div className="dashboard-header-with-back">
            <button 
              className="back-to-gallery-button"
              onClick={onClose}
              title={`Back to ${currentGalleryName}`}
            >
              ← Back to {currentGalleryName}
            </button>
            <h1>Your Galleries</h1>
          </div>
        ) : (
          <h1>Welcome to Your Gallery</h1>
        )}
        
        {isLoading ? (
          <div className="loading">
            Loading your galleries...
          </div>
        ) : activeView === 'galleries' ? (
          <div className="existing-galleries">
            <div className="gallery-header">
              <div className="gallery-controls">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search galleries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="recent">Most Recent</option>
                  <option value="name">Name</option>
                  <option value="status">Status</option>
                </select>
                <div className="toggle-container disabled" title="Coming Soon - Private galleries will be available in a future update">
                  <label className="toggle-switch disabled">
                    <input 
                      type="checkbox" 
                      checked={false}
                      disabled={true}
                      onChange={() => {}} // No-op function
                    />
                    <span className="toggle-slider disabled"></span>
                  </label>
                  <span className="toggle-label disabled">Private Only</span>
                  <span className="coming-soon-badge">Coming Soon</span>
                </div>
              </div>
              <button 
                className="create-gallery-button"
                onClick={() => setActiveView('create')}
              >
                <span className="plus-icon">+</span> Create Gallery
              </button>
            </div>
            
            {galleries.length > 0 ? (
              <div className="gallery-grid">
                {displayGalleries.map(gallery => (
                  <div 
                    key={gallery._id} 
                    className="gallery-card"
                    onClick={(e) => handleGalleryClick(gallery, e)}
                  >
                    <h3>{gallery.name}</h3>
                    <div className="gallery-info">
                      <div className={`status ${gallery.isPublic ? 'public' : 'private'}`}>
                        {gallery.isPublic ? 'Public' : 'Private'}
                      </div>
                      <div className="gallery-meta">
                        Created: {new Date(gallery.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <button 
                      className="delete-button"
                      onClick={(e) => handleDeleteClick(gallery, e)}
                      aria-label="Delete gallery"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="create-first">
                <p>You don't have any galleries yet. Create your first gallery to get started!</p>
                <button 
                  className="create-gallery-button"
                  onClick={() => setActiveView('create')}
                >
                  <span className="plus-icon">+</span> Create Gallery
                </button>
              </div>
            )}
          </div>
        ) : activeView === 'create' ? (
          <div className="create-gallery-view">
            <div className="view-header">
              <h2>Create New Gallery</h2>
              <button 
                className="back-button"
                onClick={() => setActiveView('galleries')}
              >
                ← Back to Galleries
              </button>
            </div>
            
            <form onSubmit={handleCreateSubmit}>
              <div className="name-input-group">
                <label htmlFor="modalGalleryName">Gallery Name</label>
                <input
                  id="modalGalleryName"
                  type="text"
                  value={newGalleryName}
                  onChange={(e) => setNewGalleryName(e.target.value)}
                  placeholder="Enter a name for your gallery"
                  autoFocus
                />
              </div>
              
              {/* Room Selection Tabs */}
              <div className="room-selection-tabs">
                <button 
                  className={`room-tab-button ${roomSelectionTab === 'all' ? 'active' : ''}`}
                  onClick={() => setRoomSelectionTab('all')}
                  type="button"
                >
                  All Rooms
                </button>
                <button 
                  className={`room-tab-button ${roomSelectionTab === 'owned' ? 'active' : ''}`}
                  onClick={() => setRoomSelectionTab('owned')}
                  type="button"
                >
                  My Rooms
                </button>
              </div>
              
              {/* Room Selection Grid */}
              {getFilteredRooms().length === 0 && roomSelectionTab === 'owned' ? (
                <div className="no-owned-rooms">
                  <div className="no-owned-rooms-content">
                    <h4>No Rooms Yet</h4>
                    <p>Premium rooms will be here</p>
                  </div>
                </div>
              ) : (
                getFilteredRooms().map(category => (
                  <div key={category.id}>
                    <div className="room-section-header">
                      <h3 className="room-section-title">{category.title}</h3>
                      {category.subtitle && (
                        <span className="room-section-subtitle">{category.subtitle}</span>
                      )}
                    </div>
                    {category.comingSoon ? (
                      <div className="coming-soon-section">
                        <div className="coming-soon-content">
                          <h4>Premium Rooms Coming Soon</h4>
                          <p>Custom room templates, advanced lighting, and unique gallery experiences will be available soon.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="room-grid">
                        {category.rooms.map(room => (
                          <div 
                            key={room.id} 
                            className={`room-card ${selectedRoomType === room.id ? 'selected' : ''}`}
                            onClick={() => handleRoomSelect(room.id, room.glbPath)}
                          >
                            <div className="room-thumbnail">
                              <img 
                                src={room.thumbnail} 
                                alt={room.name}
                                onError={(e) => {
                                  // Fallback to colored div if image fails to load
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div 
                                className="room-thumbnail-fallback"
                                style={{ 
                                  backgroundColor: room.id === ROOM_TYPES.CLASSIC ? '#2c3e50' : 
                                                room.id === ROOM_TYPES.SPACIOUS ? '#34495e' : 
                                                room.id === ROOM_TYPES.HEXAGONAL ? '#2c3e50' : '#1a1a1a',
                                  width: '100%',
                                  height: '100%',
                                  display: 'none'
                                }} 
                              />
                              {room.type === 'premium' && (
                                <div className="room-badge premium">Premium</div>
                              )}
                              {room.owned && room.type === 'premium' && (
                                <div className="room-badge owned">Owned</div>
                              )}
                            </div>
                            <div className="room-info">
                              <div className="room-name">{room.name}</div>
                              <div className="room-description">{room.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
              
              <div className="button-group">
                <button type="submit" className="create-button">
                  Create Gallery
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setActiveView('galleries')}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>

      {galleryToDelete && (
        <div className="delete-modal">
          <div className="modal-content">
            <h2>Delete Gallery</h2>
            <p>Are you sure you want to delete "{galleryToDelete.name}"? This action cannot be undone.</p>
            <div className="button-group">
              <button 
                className="delete-confirm-button"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
              <button 
                className="cancel-button"
                onClick={() => setGalleryToDelete(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StartDashboard; 